import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../header/Header";
import Axios from "axios";
import moment from "moment";
import { downloadWorkbook, excelDate, excelDateTime } from "../../helpers/excel";
import {
  LuAlertCircle,
  LuBanknote,
  LuCalendarX,
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuSearch,
  LuUsers,
} from "react-icons/lu";
import { UiButton, INPUT } from "../ui/ui";
import { DateRangeFilter, FilterIconButton } from "../ui/DateFilter";
const config = require("../../config/config");
const queryString = require("query-string");

const DAY = "YYYY-MM-DD";

// leaveTypes.name for type 3 is truncated in the database, so names live here
const LEAVE_TYPES = {
  1: { label: "Paid Leave", short: "Paid leave", tone: "tw-bg-primary/10 tw-text-primary" },
  2: { label: "Optional Leave", short: "Optional leave", tone: "tw-bg-amber-50 tw-text-amber-700" },
  3: {
    label: "Paid Leave Deducted Due to Missed Marking Attendance",
    short: "Missed marking (PL deducted)",
    tone: "tw-bg-orange-50 tw-text-orange-700",
  },
  4: { label: "Leave Without Pay", short: "Leave without pay", tone: "tw-bg-red-50 tw-text-red-700" },
  5: { label: "Planned Leave", short: "Planned leave", tone: "tw-bg-violet-50 tw-text-violet-700" },
};

const displayName = (name, email) => {
  if (name && String(name).trim()) return String(name).trim().replace(/\s+/g, " ");
  const source = String(email || "");
  if (!source.includes("@")) return source || "Unknown";
  return source
    .substring(0, source.lastIndexOf("@"))
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const fmt = (d) => moment(d).format("D MMM YYYY");

const SUMMARY_COLUMNS = [
  { key: "workingDays", label: "Working days", title: "Working days so far in the range (after joining date)" },
  { key: "presentMark", label: "Present" },
  { key: "workFromHome", label: "WFH", title: "Work from home" },
  { key: "outOfOffice", label: "OOO", title: "Out of office" },
  { key: "sickLeave", label: "Sick" },
  { key: "earnedLeave", label: "Paid leave", title: "Earned / paid leave days" },
  { key: "plannedLeave", label: "Planned" },
  { key: "optionalLeave", label: "Optional", title: "Optional holidays taken" },
  { key: "workWithoutPay", label: "WWP", title: "Work without pay" },
  { key: "sandwichedLeave", label: "Sandwich", title: "Weekend/holiday days between two leave days" },
  { key: "markedAfter11", label: "After 11 AM", title: "Working days marked after 11:00 AM", alert: "orange" },
  { key: "missedMark", label: "Not marked", title: "Working days that are over with no attendance", alert: "red" },
  { key: "missedMarkPlDeducted", label: "PL deducted", title: "Paid leave deducted for missed marking", alert: "orange" },
  { key: "leaveWithoutPay", label: "LWP", title: "Leave without pay (no balance left — pay deducted)", alert: "red" },
];

// Calendar month containing `date`
const monthRange = (date) => ({
  start: moment(date).startOf("month").format(DAY),
  end: moment(date).endOf("month").format(DAY),
});

// Salary cycle (26th → 25th) containing `date`
const payrollCycle = (date) => {
  const d = moment(date);
  const end = d.date() >= 26 ? d.clone().add(1, "month").date(25) : d.clone().date(25);
  return {
    start: end.clone().subtract(1, "month").date(26).format(DAY),
    end: end.format(DAY),
  };
};

const TABS = {
  summary: {
    label: "Attendance summary",
    defaultRange: () => monthRange(moment()),
    shift: (range, n) => monthRange(moment(range.start).add(n, "month")),
    isPreset: (r) => {
      const m = monthRange(r.start);
      return m.start === r.start && m.end === r.end;
    },
    describe: (r) => moment(r.start).format("MMMM YYYY"),
    unit: "month",
  },
  ledger: {
    label: "Leave ledger",
    defaultRange: () => payrollCycle(moment()),
    shift: (range, n) => payrollCycle(moment(range.end).add(n, "month")),
    isPreset: (r) => {
      const c = payrollCycle(r.end);
      return c.start === r.start && c.end === r.end;
    },
    describe: (r) => `Salary cycle ${fmt(r.start)} – ${fmt(r.end)}`,
    unit: "cycle",
  },
};

const Report = () => {
  const [tab, setTab] = useState("summary");
  // Each tab keeps its own range: calendar month vs. 26th→25th salary cycle
  const [ranges, setRanges] = useState({
    summary: TABS.summary.defaultRange(),
    ledger: TABS.ledger.defaultRange(),
  });
  const config_ = TABS[tab];
  const range = ranges[tab];
  const setRange = (next) => setRanges((prev) => ({ ...prev, [tab]: next }));
  const isCurrent =
    JSON.stringify(range) === JSON.stringify(config_.defaultRange());

  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className="tw-mx-auto tw-max-w-7xl tw-py-4 tw-font-sans tw-text-foreground sm:tw-py-6">
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
            <div>
              <h1 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-foreground">
                Reports
              </h1>
              <div className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
                {tab === "summary" ? "Attendance for " : "Leave for "}
                <span className="tw-font-medium tw-text-foreground">
                  {config_.isPreset(range)
                    ? config_.describe(range)
                    : `${fmt(range.start)} – ${fmt(range.end)}`}
                </span>
              </div>
            </div>
            <RangePicker
              range={range}
              unit={config_.unit}
              onChange={setRange}
              onShift={(n) => setRange(config_.shift(range, n))}
              onReset={isCurrent ? null : () => setRange(config_.defaultRange())}
              presets={
                tab === "summary"
                  ? [
                      { label: "This month", range: monthRange(moment()) },
                      { label: "Last month", range: monthRange(moment().subtract(1, "month")) },
                    ]
                  : [
                      { label: "Current cycle", range: payrollCycle(moment()) },
                      { label: "Last cycle", range: payrollCycle(moment().subtract(1, "month")) },
                    ]
              }
            />
          </div>

          <div className="tw-mb-5 tw-inline-flex tw-gap-1 tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-soft" role="tablist">
            {Object.entries(TABS).map(([key, t]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`tw-h-8 tw-rounded-md tw-border-0 tw-px-3 tw-text-sm tw-font-medium tw-transition-colors ${
                  tab === key
                    ? "tw-bg-primary/10 tw-text-primary"
                    : "tw-bg-transparent tw-text-muted-foreground hover:tw-text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "summary" ? <AttendanceSummary range={range} /> : <LeaveLedger range={range} />}
        </div>
      </div>
    </>
  );
};

// ◀ [date range filter] ▶ in the Reporting Dashboard filter style
const RangePicker = ({ range, unit, onChange, onShift, onReset, presets }) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <FilterIconButton aria-label={`Previous ${unit}`} title={`Previous ${unit}`} onClick={() => onShift(-1)}>
      <LuChevronLeft aria-hidden="true" />
    </FilterIconButton>
    <DateRangeFilter
      value={range}
      onChange={onChange}
      onReset={onReset}
      presets={presets}
      align="end"
      label="Date range"
    />
    <FilterIconButton aria-label={`Next ${unit}`} title={`Next ${unit}`} onClick={() => onShift(1)}>
      <LuChevronRight aria-hidden="true" />
    </FilterIconButton>
  </div>
);

/* ------------------------------------------------------------------ */
/* Attendance summary                                                  */
/* ------------------------------------------------------------------ */

const AttendanceSummary = ({ range }) => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    Axios.get(
      config.API_BASE +
        "/user/getUserAttendanceReport?" +
        queryString.stringify({ dateBetween: `${range.start}|${range.end}` }),
    )
      .then((response) => {
        const res = response.data;
        if (res.status) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setMeta(res.meta || null);
        } else {
          setRows([]);
          setError(res.msg || "Couldn't load the report");
        }
      })
      .catch(() => {
        setRows([]);
        setError("Couldn't load the report");
      })
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  const hasIssue = (r) => r.missedMark > 0 || r.leaveWithoutPay > 0 || r.missedMarkPlDeducted > 0;
  const q = search.trim().toLowerCase();
  const visible = rows.filter(
    (r) =>
      (!onlyIssues || hasIssue(r)) &&
      (!q ||
        displayName(r.name, r.email).toLowerCase().includes(q) ||
        String(r.email || "").toLowerCase().includes(q)),
  );
  const totals = rows.reduce(
    (acc, r) => ({
      notMarked: acc.notMarked + (r.missedMark || 0),
      pl: acc.pl + (r.missedMarkPlDeducted || 0),
      lwp: acc.lwp + (r.leaveWithoutPay || 0),
      people: acc.people + (hasIssue(r) ? 1 : 0),
    }),
    { notMarked: 0, pl: 0, lwp: 0, people: 0 },
  );

  const exportToExcel = () => {
    if (!visible.length) return;
    const period = `${fmt(range.start)} – ${fmt(range.end)}`;
    downloadWorkbook(`Attendance_summary_${range.start}_to_${range.end}.xlsx`, [
      {
        name: "Attendance summary",
        rows: visible,
        columns: [
          { header: "Employee", width: 26, value: (r) => displayName(r.name, r.email) },
          { header: "Email", width: 38, value: (r) => r.email },
          { header: "Joining date", width: 14, type: "date", value: (r) => excelDate(r.joiningDate) },
          { header: "Working days", width: 14, type: "number", value: (r) => r.workingDays },
          { header: "Present", width: 10, type: "number", value: (r) => r.presentMark },
          { header: "Work from home", width: 16, type: "number", value: (r) => r.workFromHome },
          { header: "Out of office", width: 14, type: "number", value: (r) => r.outOfOffice },
          { header: "Sick leave", width: 12, type: "number", value: (r) => r.sickLeave },
          { header: "Paid leave", width: 12, type: "number", value: (r) => r.earnedLeave },
          { header: "Planned leave", width: 14, type: "number", value: (r) => r.plannedLeave },
          { header: "Optional leave", width: 15, type: "number", value: (r) => r.optionalLeave },
          { header: "Work without pay", width: 17, type: "number", value: (r) => r.workWithoutPay },
          { header: "Sandwiched leave", width: 17, type: "number", value: (r) => r.sandwichedLeave },
          { header: "Marked after 11 AM", width: 19, type: "number", value: (r) => r.markedAfter11 },
          { header: "Not marked", width: 12, type: "number", value: (r) => r.missedMark },
          { header: "Paid Leave deducted (missed marking)", width: 34, type: "number", value: (r) => r.missedMarkPlDeducted },
          { header: "Leave without pay", width: 18, type: "number", value: (r) => r.leaveWithoutPay },
          { header: "Worked on weekend/holiday", width: 26, type: "number", value: (r) => r.weekendWork },
          { header: "Period", width: 26, value: () => period },
        ],
      },
    ]);
  };

  return (
    <div className="tw-space-y-5">
      {meta && (
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-text-xs">
          {[
            `${meta.totalDays} days`,
            `${meta.workingDays} working days`,
            `${meta.weekEnds} weekend days`,
            `${meta.compulsoryHolidays} public ${meta.compulsoryHolidays === 1 ? "holiday" : "holidays"}`,
            meta.countedUntil < meta.endDate
              ? `Counted until ${fmt(meta.countedUntil)} (${meta.elapsedWorkingDays} working days so far)`
              : null,
          ]
            .filter(Boolean)
            .map((label) => (
              <span
                key={label}
                className="tw-rounded-full tw-border tw-border-solid tw-border-border tw-bg-white tw-px-2.5 tw-py-1 tw-font-medium tw-text-muted-foreground"
              >
                {label}
              </span>
            ))}
        </div>
      )}

      <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-gap-4 lg:tw-grid-cols-4">
        <Stat icon={LuUsers} tone="tw-bg-primary/10 tw-text-primary" label="People" value={rows.length} />
        <Stat
          icon={LuCalendarX}
          tone="tw-bg-red-50 tw-text-red-600"
          label="Days not marked"
          value={totals.notMarked}
          detail={`${totals.people} ${totals.people === 1 ? "person" : "people"} affected`}
        />
        <Stat icon={LuAlertCircle} tone="tw-bg-orange-50 tw-text-orange-600" label="PL deducted" value={totals.pl} detail="for missed marking" />
        <Stat icon={LuBanknote} tone="tw-bg-red-50 tw-text-red-600" label="Leave without pay" value={totals.lwp} detail="pay deducted" />
      </div>

      <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft">
        <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-p-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <div className="tw-relative sm:tw-w-64">
              <LuSearch className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
              <input
                type="search"
                className={`${INPUT} tw-pl-9`}
                placeholder="Search people"
                aria-label="Search people"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Toggle checked={onlyIssues} onChange={setOnlyIssues} label="Only missed days / LWP" />
          </div>
          <UiButton variant="outline" onClick={exportToExcel} disabled={loading || visible.length === 0}>
            <LuDownload aria-hidden="true" />
            Export to Excel
          </UiButton>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <EmptyState text={error} />
        ) : visible.length === 0 ? (
          <EmptyState text={onlyIssues ? "No one missed marking or had pay deducted. 🎉" : "No people match your search."} />
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-bg-secondary/60 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                  <th className="tw-sticky tw-left-0 tw-z-10 tw-bg-[hsl(210_40%_97%)] tw-px-5 tw-py-2.5 tw-text-left tw-font-semibold">
                    Person
                  </th>
                  {SUMMARY_COLUMNS.map((c) => (
                    <th key={c.key} title={c.title || c.label} className="tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-center tw-font-semibold">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const name = displayName(r.name, r.email);
                  return (
                    <tr key={r.userId || r.email} className="tw-group tw-border-0 tw-border-t tw-border-solid tw-border-border hover:tw-bg-secondary/40">
                      <td className="tw-sticky tw-left-0 tw-z-10 tw-bg-white tw-px-5 tw-py-3 group-hover:tw-bg-[hsl(210_40%_98%)]">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gradient-primary tw-text-[11px] tw-font-semibold tw-text-white" aria-hidden="true">
                            {getInitials(name)}
                          </span>
                          <div className="tw-min-w-0">
                            <div className="tw-whitespace-nowrap tw-font-medium tw-text-foreground">{name}</div>
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-text-xs tw-text-muted-foreground">
                              {r.joinedInRange ? `Joined ${fmt(r.joiningDate)}` : r.email}
                              {r.pendingToday && (
                                <span className="tw-rounded-full tw-bg-secondary tw-px-1.5 tw-text-[10px] tw-font-semibold">
                                  today pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      {SUMMARY_COLUMNS.map((c) => {
                        const v = r[c.key] || 0;
                        const alert = c.alert && v > 0;
                        return (
                          <td key={c.key} className="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-center">
                            <span
                              className={`${COUNT_BOX} ${
                                alert
                                  ? `tw-font-semibold ${c.alert === "red" ? "tw-bg-red-50 tw-text-red-700" : "tw-bg-orange-50 tw-text-orange-700"}`
                                  : v === 0
                                    ? "tw-font-medium tw-text-muted-foreground/60"
                                    : "tw-text-sm tw-font-medium tw-text-foreground"
                              }`}
                            >
                              {v}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="tw-text-xs tw-leading-5 tw-text-muted-foreground">
        Working days exclude weekends and public holidays, and start from each person's joining date. "Not
        marked" counts only days that are over. When a day isn't marked, paid leave is deducted if
        available; otherwise it becomes leave without pay.
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Leave ledger                                                        */
/* ------------------------------------------------------------------ */

const LEDGER_FILTERS = [
  { key: "all", label: "All leave" },
  { key: "3", label: "Missed marking" },
  { key: "4", label: "Leave without pay" },
  { key: "1", label: "Paid leave" },
  { key: "2", label: "Optional leave" },
  { key: "5", label: "Planned leave" },
];

const isAutoEntry = (r) => [3, 4].includes(Number(r.leaveTypeId));
const ledgerColumns = (period) => [
  { header: "Employee", width: 26, value: (r) => displayName(r.name, r.email) },
  { header: "Email", width: 38, value: (r) => r.email },
  { header: "Employee status", width: 16, value: (r) => (String(r.ifActive) === "0" ? "Inactive" : "Active") },
  { header: "Leave type", width: 34, value: (r) => LEAVE_TYPES[r.leaveTypeId]?.short || r.leaveType || "" },
  { header: "From", width: 14, type: "date", value: (r) => excelDate(r.startDate) },
  { header: "To", width: 14, type: "date", value: (r) => excelDate(r.endDate || r.startDate) },
  { header: "Days", width: 8, type: "number", value: (r) => (Number(r.transactionType) === 2 ? 1 : -1) * (Number(r.noOfDays) || 1) },
  { header: "Credit / debit", width: 14, value: (r) => (Number(r.transactionType) === 2 ? "Credit" : "Debit") },
  { header: "Salary impact", width: 18, value: (r) => (Number(r.leaveTypeId) === 4 && Number(r.transactionType) !== 2 ? "Salary deduction" : "None") },
  { header: "Source", width: 30, value: (r) => (isAutoEntry(r) ? "Auto — attendance not marked" : Number(r.transactionType) === 2 ? "Credited" : "Applied by employee") },
  { header: "Status", width: 12, value: (r) => (String(r.isCancelled) === "1" || r.isCancelled === true ? "Cancelled" : "Active") },
  { header: "Employee comment", width: 40, value: (r) => (isAutoEntry(r) || !r.comment || r.comment === "-" ? "" : r.comment) },
  { header: "Applied on", width: 20, type: "datetime", value: (r) => excelDateTime(r.appliedDate) },
  { header: "Period", width: 26, value: () => period },
];

const LeaveLedger = ({ range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCredits, setShowCredits] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    Axios.get(
      config.API_BASE +
        "/leave/getLeaveLedger?" +
        queryString.stringify({ dateBetween: `${range.start}|${range.end}` }),
    )
      .then((response) => {
        const res = response.data;
        if (res.status) setRows(Array.isArray(res.data) ? res.data : []);
        else {
          setRows([]);
          setError(res.msg || "Couldn't load the leave ledger");
        }
      })
      .catch(() => {
        setRows([]);
        setError("Couldn't load the leave ledger. The API may need to be updated to the latest version.");
      })
      .finally(() => setLoading(false));
  }, [range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  const isCancelled = (r) => String(r.isCancelled) === "1" || r.isCancelled === true;
  const isCredit = (r) => Number(r.transactionType) === 2;

  // Base set: debits that still count (what payroll cares about), unless toggled
  const base = rows.filter((r) => (showCredits || !isCredit(r)) && (showCancelled || !isCancelled(r)));
  const q = search.trim().toLowerCase();
  const visible = base.filter(
    (r) =>
      (filter === "all" || String(r.leaveTypeId) === filter) &&
      (!q ||
        displayName(r.name, r.email).toLowerCase().includes(q) ||
        String(r.email || "").toLowerCase().includes(q)),
  );

  const countFor = (key) =>
    key === "all" ? base.length : base.filter((r) => String(r.leaveTypeId) === key).length;

  // Who missed marking / had pay deducted — counted debits only
  const payroll = useMemo(() => {
    const byPerson = new Map();
    rows
      .filter((r) => !isCredit(r) && !isCancelled(r) && [3, 4].includes(Number(r.leaveTypeId)))
      .forEach((r) => {
        const key = r.userId;
        const p = byPerson.get(key) || {
          userId: r.userId,
          name: displayName(r.name, r.email),
          email: r.email,
          active: String(r.ifActive) !== "0",
          missed: 0,
          lwp: 0,
          dates: [],
        };
        if (Number(r.leaveTypeId) === 3) p.missed += Number(r.noOfDays) || 1;
        else p.lwp += Number(r.noOfDays) || 1;
        p.dates.push({ date: r.startDate, type: Number(r.leaveTypeId) });
        byPerson.set(key, p);
      });
    return [...byPerson.values()].sort((a, b) => b.missed + b.lwp - (a.missed + a.lwp) || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const exportLedger = () => {
    if (!visible.length) return;
    const period = `${fmt(range.start)} – ${fmt(range.end)}`;
    downloadWorkbook(`Leave_ledger_${range.start}_to_${range.end}.xlsx`, [
      { name: "Leave ledger", rows: visible, columns: ledgerColumns(period) },
    ]);
  };

  const exportPayroll = () => {
    if (!payroll.length) return;
    const period = `${fmt(range.start)} – ${fmt(range.end)}`;
    const datesOf = (p, type) =>
      p.dates
        .filter((d) => d.type === type)
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((d) => moment(d.date).format("D MMM"))
        .join(", ");
    const counted = rows.filter((r) => !isCredit(r) && !isCancelled(r) && isAutoEntry(r));
    downloadWorkbook(`Payroll_leave_report_${range.start}_to_${range.end}.xlsx`, [
      {
        name: "Summary",
        rows: payroll,
        columns: [
          { header: "Employee", width: 26, value: (p) => p.name },
          { header: "Email", width: 38, value: (p) => p.email },
          { header: "Employee status", width: 16, value: (p) => (p.active ? "Active" : "Inactive") },
          { header: "Leave without pay (days)", width: 24, type: "number", value: (p) => p.lwp },
          { header: "Paid Leave deducted (days)", width: 26, type: "number", value: (p) => p.missed },
          { header: "Total missed days", width: 18, type: "number", value: (p) => p.missed + p.lwp },
          { header: "Leave without pay dates", width: 30, value: (p) => datesOf(p, 4) },
          { header: "Paid Leave deducted dates", width: 30, value: (p) => datesOf(p, 3) },
          { header: "Salary impact", width: 20, value: (p) => (p.lwp ? `Deduct ${p.lwp} day${p.lwp === 1 ? "" : "s"}` : "None") },
          { header: "Period", width: 26, value: () => period },
        ],
      },
      { name: "Entries", rows: counted, columns: ledgerColumns(period) },
    ]);
  };

  return (
    <div className="tw-space-y-5">
      {/* Monthly summary to share: who missed marking / had pay deducted */}
      <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-red-100 tw-bg-card tw-shadow-soft">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-red-100 tw-bg-red-50/50 tw-px-5 tw-py-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-red-100 tw-text-red-600">
              <LuBanknote aria-hidden="true" />
            </div>
            <div>
              <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-foreground">Missed marking & leave without pay</h2>
              <div className="tw-text-xs tw-text-muted-foreground">
                {payroll.length} {payroll.length === 1 ? "person" : "people"} · PL is deducted for a missed day; with no PL
                left it becomes leave without pay
              </div>
            </div>
          </div>
          <UiButton variant="outline" size="sm" onClick={exportPayroll} disabled={loading || payroll.length === 0}>
            <LuDownload aria-hidden="true" />
            Export summary
          </UiButton>
        </div>
        {loading ? (
          <TableSkeleton rows={3} />
        ) : payroll.length === 0 ? (
          <EmptyState text={error || "No missed marking or leave without pay in this period. 🎉"} />
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-bg-secondary/60 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                  <th className="tw-px-5 tw-py-2.5 tw-font-semibold">Person</th>
                  <th className="tw-w-32 tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-center tw-font-semibold">PL deducted</th>
                  <th className="tw-w-24 tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-center tw-font-semibold">LWP</th>
                  <th className="tw-px-5 tw-py-2.5 tw-font-semibold">Dates</th>
                </tr>
              </thead>
              <tbody>
                {payroll.map((p) => (
                  <tr key={p.userId} className="tw-border-0 tw-border-t tw-border-solid tw-border-border">
                    <td className="tw-px-5 tw-py-3">
                      <div className="tw-whitespace-nowrap tw-font-medium tw-text-foreground">{p.name}</div>
                      <div className="tw-whitespace-nowrap tw-text-xs tw-text-muted-foreground">{p.email}</div>
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-text-center">
                      <CountBadge value={p.missed} tone="tw-bg-orange-50 tw-text-orange-700" />
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-text-center">
                      <CountBadge value={p.lwp} tone="tw-bg-red-50 tw-text-red-700" />
                    </td>
                    <td className="tw-px-5 tw-py-3">
                      <div className="tw-flex tw-flex-wrap tw-gap-1">
                        {p.dates
                          .sort((a, b) => a.date.localeCompare(b.date))
                          .map((d, i) => (
                            <span
                              key={i}
                              className={`tw-rounded-full tw-px-2 tw-py-px tw-text-[11px] tw-font-medium ${
                                d.type === 4 ? "tw-bg-red-50 tw-text-red-700" : "tw-bg-orange-50 tw-text-orange-700"
                              }`}
                              title={d.type === 4 ? "Leave without pay" : "PL deducted"}
                            >
                              {moment(d.date).format("D MMM")}
                            </span>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Full ledger */}
      <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft">
        <div className="tw-space-y-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-p-4">
          <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
            <div className="tw-relative sm:tw-w-64">
              <LuSearch className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
              <input
                type="search"
                className={`${INPUT} tw-pl-9`}
                placeholder="Search people"
                aria-label="Search people"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
              <Toggle checked={showCredits} onChange={setShowCredits} label="Show credits" />
              <Toggle checked={showCancelled} onChange={setShowCancelled} label="Show cancelled" />
              <UiButton variant="outline" onClick={exportLedger} disabled={loading || visible.length === 0}>
                <LuDownload aria-hidden="true" />
                Export to Excel
              </UiButton>
            </div>
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-label="Filter by leave type">
            {LEDGER_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-xs tw-font-medium tw-transition-colors ${
                  filter === f.key
                    ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                    : "tw-border-border tw-bg-white tw-text-foreground hover:tw-bg-secondary"
                }`}
              >
                {f.label}
                <span className="tw-text-muted-foreground">{countFor(f.key)}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <EmptyState text={error} />
        ) : visible.length === 0 ? (
          <EmptyState text="No leave entries match these filters." />
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-bg-secondary/60 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-2.5 tw-font-semibold">Date</th>
                  <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Person</th>
                  <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Type</th>
                  <th className="tw-w-20 tw-px-3 tw-py-2.5 tw-text-center tw-font-semibold">Days</th>
                  <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Comment</th>
                  <th className="tw-whitespace-nowrap tw-px-5 tw-py-2.5 tw-font-semibold">Applied on</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const type = LEAVE_TYPES[r.leaveTypeId];
                  const cancelled = isCancelled(r);
                  return (
                    <tr key={r.id} className={`tw-border-0 tw-border-t tw-border-solid tw-border-border hover:tw-bg-secondary/40 ${cancelled ? "tw-opacity-60" : ""}`}>
                      <td className="tw-whitespace-nowrap tw-px-5 tw-py-3 tw-align-top">
                        <div className="tw-font-medium tw-text-foreground">{fmt(r.startDate)}</div>
                        {r.endDate && r.endDate !== r.startDate && !isCredit(r) && (
                          <div className="tw-text-xs tw-text-muted-foreground">to {moment(r.endDate).format("D MMM")}</div>
                        )}
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-align-top">
                        <div className="tw-whitespace-nowrap tw-font-medium tw-text-foreground">{displayName(r.name, r.email)}</div>
                        <div className="tw-whitespace-nowrap tw-text-xs tw-text-muted-foreground">
                          {r.email}
                          {String(r.ifActive) === "0" && " · inactive"}
                        </div>
                      </td>
                      <td className="tw-px-3 tw-py-3 tw-align-top">
                        <span className={`tw-inline-block tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${type?.tone || "tw-bg-secondary tw-text-muted-foreground"}`}>
                          {type?.short || r.leaveType || "Other"}
                        </span>
                        {(isCredit(r) || cancelled) && (
                          <div className="tw-mt-1 tw-text-[11px] tw-font-medium tw-text-muted-foreground">
                            {isCredit(r) ? "Credit" : ""}
                            {isCredit(r) && cancelled ? " · " : ""}
                            {cancelled ? "Cancelled" : ""}
                          </div>
                        )}
                      </td>
                      <td className="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-center tw-align-top tw-font-semibold tw-tabular-nums">
                        <span className={isCredit(r) ? "tw-text-emerald-700" : "tw-text-foreground"}>
                          {isCredit(r) ? "+" : ""}
                          {r.noOfDays}
                        </span>
                      </td>
                      <td className="tw-max-w-[280px] tw-px-3 tw-py-3 tw-align-top tw-text-muted-foreground">
                        <div className="tw-break-words">{r.comment && r.comment !== "-" ? r.comment : "—"}</div>
                      </td>
                      <td className="tw-whitespace-nowrap tw-px-5 tw-py-3 tw-align-top tw-text-muted-foreground">
                        {r.appliedDate ? moment(r.appliedDate).format("D MMM, h:mm a") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ */

const Stat = ({ icon: Icon, tone, label, value, detail }) => (
  <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-p-4 tw-shadow-soft sm:tw-p-5">
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
      <div className="tw-text-sm tw-font-medium tw-text-muted-foreground">{label}</div>
      <div className={`tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${tone}`}>
        <Icon aria-hidden="true" />
      </div>
    </div>
    <div className="tw-mt-2 tw-text-3xl tw-font-semibold tw-leading-9 tw-text-foreground">{value}</div>
    {detail && <div className="tw-mt-1 tw-text-xs tw-text-muted-foreground">{detail}</div>}
  </div>
);

// Every count sits in the same fixed-width box (zeros just have no fill),
// so badged and plain numbers line up in a column.
const COUNT_BOX =
  "tw-inline-flex tw-h-6 tw-min-w-[32px] tw-items-center tw-justify-center tw-rounded-md tw-px-1.5 tw-text-xs tw-tabular-nums";

const CountBadge = ({ value, tone }) => (
  <span
    className={`${COUNT_BOX} ${
      value > 0 ? `tw-font-semibold ${tone}` : "tw-font-medium tw-text-muted-foreground/60"
    }`}
  >
    {value}
  </span>
);

// Switch-style toggle (native checkboxes are unstyled by the global CSS)
const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="tw-inline-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-foreground"
  >
    <span
      className={`tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-shrink-0 tw-items-center tw-rounded-full tw-transition-colors ${
        checked ? "tw-bg-primary" : "tw-bg-slate-200"
      }`}
      aria-hidden="true"
    >
      <span
        className={`tw-absolute tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow-soft tw-transition-transform ${
          checked ? "tw-translate-x-[18px]" : "tw-translate-x-0.5"
        }`}
      />
    </span>
    {label}
  </button>
);

const TableSkeleton = ({ rows = 5 }) => (
  <div className="tw-space-y-2 tw-p-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="tw-h-10 tw-animate-pulse tw-rounded-md tw-bg-secondary" />
    ))}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-muted-foreground">{text}</div>
);

export default Report;
